import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [dealId, setDealId] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDealId(data.deal_id);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch {}
      attempts++;
      if (attempts >= 8) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#D97757]" />
          <h1 className="font-serif-display text-3xl mt-6">Confirming your payment…</h1>
          <p className="text-[#5F6C60] mt-2">This usually takes just a few seconds.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-12 h-12 mx-auto text-[#3E5D4A]" strokeWidth={1.5} />
          <h1 className="font-serif-display text-4xl mt-6">Your deal is live</h1>
          <p className="text-[#5F6C60] mt-2">It will stay published for the next 30 days. Customers can find and contact you now.</p>
          <div className="mt-8 flex gap-3 justify-center">
            <Button data-testid="success-dashboard" onClick={()=>navigate("/dashboard")} className="rounded-full bg-[#D97757] hover:bg-[#C26243] text-white px-6 h-12">Go to dashboard</Button>
            {dealId && <Link to={`/deals/${dealId}`}><Button variant="outline" className="rounded-full h-12 px-6">View live deal</Button></Link>}
          </div>
        </>
      )}
      {(status === "expired" || status === "error" || status === "timeout") && (
        <>
          <AlertCircle className="w-12 h-12 mx-auto text-[#9D5C5C]" strokeWidth={1.5} />
          <h1 className="font-serif-display text-3xl mt-6">{status === "timeout" ? "Still confirming…" : "Payment not completed"}</h1>
          <p className="text-[#5F6C60] mt-2">Head back to your dashboard. If you were charged, your deal will activate shortly.</p>
          <Button onClick={()=>navigate("/dashboard")} className="mt-6 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white px-6 h-12">Back to dashboard</Button>
        </>
      )}
    </div>
  );
}
